import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { ACCOUNT_VALIDATION_POLL_MS } from '@/constants/personas';
import { api } from '@/lib/api';
import { formatRelative } from '@/lib/format';
import type { Account, ProfileFieldOutcome } from '@/types/account';

const OUTCOME_TONE: Record<ProfileFieldOutcome, 'success' | 'neutral' | 'failure'> = {
  applied: 'success',
  unchanged: 'neutral',
  skipped: 'neutral',
  failed: 'failure',
};

const FIELD_LABEL = { avatar: 'Photo', bio: 'Bio', name: 'Name' } as const;

/**
 * Profile setup + first metrics for one linked IG account: sets the live
 * name / bio (with the AI-disclosure line) / photo from the persona, and shows
 * followers now vs. the first reading.
 */
export function ProfileSyncRow({ account, personaId }: { account: Account; personaId: string }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [workflowId, setWorkflowId] = useState<string | null>(null);

  const briefQ = useQuery({
    queryKey: ['persona', personaId, 'brief'],
    queryFn: () => api.getPersonaBrief(personaId),
    enabled: confirming,
  });

  const wfQ = useQuery({
    queryKey: ['workflow', workflowId],
    queryFn: () => api.getWorkflow(workflowId!),
    enabled: !!workflowId,
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      return status && status !== 'RUNNING' ? false : ACCOUNT_VALIDATION_POLL_MS;
    },
  });
  const wfStatus = wfQ.data?.status;
  useEffect(() => {
    if (workflowId && wfStatus && wfStatus !== 'RUNNING') {
      void queryClient.invalidateQueries({ queryKey: ['persona', personaId, 'accounts'] });
      setWorkflowId(null);
      if (wfStatus !== 'COMPLETED') toast.error(`Profile run ${wfStatus.toLowerCase()}`);
    }
  }, [workflowId, wfStatus, personaId, queryClient]);

  const onStarted = (label: string) => (res: { workflow_id: string }) => {
    toast.success(label);
    setWorkflowId(res.workflow_id);
    setConfirming(false);
  };
  const sync = useMutation({
    mutationFn: () => api.syncAccountProfile(account.account_id),
    onSuccess: onStarted('Updating the Instagram profile…'),
    onError: (err) => toast.error('Profile sync failed to start', { description: String(err) }),
  });
  const stats = useMutation({
    mutationFn: () => api.refreshAccountStats(account.account_id),
    onSuccess: onStarted('Reading followers…'),
    onError: (err) => toast.error('Stats refresh failed to start', { description: String(err) }),
  });

  const state = account.profile_state ?? {};
  const latest = state.latest;
  const baseline = state.baseline;
  const delta =
    latest?.followers != null && baseline?.followers != null
      ? latest.followers - baseline.followers
      : null;
  const fields = state.last_sync?.fields;
  const busy = !!workflowId || sync.isPending || stats.isPending;

  return (
    <div className="space-y-2 border-t border-border pt-3 text-xs">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-muted-foreground">
          {latest ? (
            <>
              <span>
                <span className="font-medium text-foreground">{latest.followers ?? '—'}</span>{' '}
                followers
                {delta !== null && delta !== 0 && (
                  <span className={delta > 0 ? 'text-emerald-600' : 'text-red-600'}>
                    {' '}
                    ({delta > 0 ? '+' : ''}
                    {delta} since {formatRelative(baseline!.read_at)})
                  </span>
                )}
              </span>
              <span>
                <span className="font-medium text-foreground">{latest.post_count ?? '—'}</span>{' '}
                posts
              </span>
              <span>read {formatRelative(latest.read_at)}</span>
              {latest.is_private && <Badge tone="warning">private account</Badge>}
            </>
          ) : (
            <span>No profile reading yet.</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => stats.mutate()}>
            Refresh stats
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => setConfirming(true)}>
            Set up profile
          </Button>
        </div>
      </div>

      {state.last_sync && (
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-1.5 text-muted-foreground">
            <span>Last profile setup {formatRelative(state.last_sync.synced_at)}:</span>
            {fields ? (
              (Object.keys(FIELD_LABEL) as Array<keyof typeof FIELD_LABEL>).map((k) => (
                <Badge key={k} tone={OUTCOME_TONE[fields[k].outcome]} title={fields[k].detail}>
                  {FIELD_LABEL[k]} {fields[k].outcome}
                </Badge>
              ))
            ) : (
              <Badge tone="failure">failed</Badge>
            )}
          </div>
          {state.last_sync.error && <p className="text-red-600">{state.last_sync.error}</p>}
          {fields &&
            (Object.keys(FIELD_LABEL) as Array<keyof typeof FIELD_LABEL>)
              .filter((k) => fields[k].outcome === 'failed' && fields[k].detail)
              .map((k) => (
                <p key={k} className="break-all text-muted-foreground">
                  {FIELD_LABEL[k]}: {fields[k].detail!.slice(0, 300)}
                </p>
              ))}
        </div>
      )}

      {latest?.is_private && (
        <p className="text-amber-700 dark:text-amber-300">
          The account is private — switch it to public in the Instagram app so the page is visible.
        </p>
      )}

      {confirming && (
        <ConfirmDialog
          title={`Update @${account.handle} on Instagram?`}
          confirmLabel="Update profile"
          pending={sync.isPending}
          onCancel={() => setConfirming(false)}
          onConfirm={() => sync.mutate()}
          description={
            briefQ.data ? (
              <div className="space-y-2">
                <p>Ghost will log in and set these on the live profile:</p>
                <p>
                  <span className="font-medium text-foreground">Name:</span>{' '}
                  {briefQ.data.profile_name}
                </p>
                <p className="font-medium text-foreground">Bio:</p>
                <pre className="whitespace-pre-wrap rounded-md bg-muted p-2 font-sans text-xs text-foreground">
                  {briefQ.data.profile_bio}
                </pre>
                <p>
                  <span className="font-medium text-foreground">Photo:</span> the persona's hero
                  image.
                </p>
                <p className="text-xs">
                  Instagram allows two name changes per 14 days; a refused name change doesn't block
                  the bio or photo.
                </p>
              </div>
            ) : (
              'Loading what will be written…'
            )
          }
        />
      )}
    </div>
  );
}
